import React, { useLayoutEffect, useRef } from 'react'
import { BtnV2, l } from '@manychat/manyui'

import { Heading } from 'apps/aiTextImprover/components/Heading/Heading'
import { ImprovementVariant } from 'apps/aiTextImprover/components/ImprovementVariant/ImprovementVariant'
import { StepLayout } from 'apps/aiTextImprover/components/StepLayout/StepLayout'
import { useImprovements } from 'apps/aiTextImprover/components/Steps/ChooseImprovementStep/hooks/useImprovements'
import { AiTextImproverAnalytics } from 'apps/aiTextImprover/lib/analytics'
import { ImproveTextRequestData } from 'shared/api/requests/aiContentGenerator/schemas'
import { useDidMountEffect } from 'utils/commonHooks'

import cm from './ChooseImprovementStep.module.css'

interface ChooseImprovementStepProps {
  title: string
  generationData: ImproveTextRequestData
  onSelect: (improvedText: string) => void
  handleBack: () => void
}

export const ChooseImprovementStep = ({
  title,
  generationData,
  onSelect,
  handleBack,
}: ChooseImprovementStepProps) => {
  const { generate, isGenerating, variants } = useImprovements({
    generationData,
  })

  const lastImprovementVariantRef = useRef<HTMLElement | null>(null)
  const scrollerRef = useRef<HTMLDivElement | null>(null)

  useDidMountEffect(() => {
    generate()
  })

  useLayoutEffect(
    function scrollToLastImprovement() {
      if (!scrollerRef.current || !lastImprovementVariantRef.current) {
        return
      }

      scrollerRef.current.scroll({
        top: lastImprovementVariantRef.current.offsetTop - scrollerRef.current.offsetTop,
        behavior: 'smooth',
      })
    },
    [variants],
  )

  const selectImprovement = (improvedVariant: string, index: number) => {
    onSelect(improvedVariant)

    AiTextImproverAnalytics.logVariantSubmit({
      goal: generationData.goal,
      goalValue: generationData.goal_value,
      sourceText: generationData.source,
      improvedText: improvedVariant,
      index,
    })
  }

  return (
    <StepLayout
      widgetTitle={<Heading onBackClick={handleBack} label={title} />}
      footer={
        variants.length > 0 ? (
          <BtnV2 loading={isGenerating} onClick={generate} fullWidth variant="default">
            {l.translate('Generate More')}
          </BtnV2>
        ) : null
      }
      isLoading={isGenerating}
      showContentOnLoading={variants.length > 0}
      showFooterOnLoading={variants.length > 0}
      scrollerRef={scrollerRef}
    >
      <p className="visually-hidden">
        {l.translate('Choose generated improvement or generate more variants')}
      </p>

      {variants.length > 0 && (
        <div className={cm.variantsList}>
          {variants.map((variant, index, { length }) => (
            <ImprovementVariant
              ref={index + 1 === length ? lastImprovementVariantRef : null}
              key={index}
              onSelect={() => selectImprovement(variant.text, index)}
              text={variant.text}
            />
          ))}
        </div>
      )}
    </StepLayout>
  )
}
